import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useAuth } from './AuthContext.jsx';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const STORAGE_KEY = 'processorJobIds';
const POLLING_INTERVAL_MS = 5000;

const JobStatusContext = createContext(null);

export const useJobStatus = () => {
  const context = useContext(JobStatusContext);
  if (!context) {
    throw new Error('useJobStatus must be used within a JobStatusProvider');
  }
  return context;
};

export const JobStatusProvider = ({ children }) => {
  const { isAuthenticated, fetchWithAuth } = useAuth();
  const queryClient = useQueryClient();
  const [jobOrder, setJobOrder] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Load from session storage
  useEffect(() => {
    try {
      const stored = window.sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) setJobOrder(parsed);
      }
    } catch (error) {
      console.error('Failed to parse stored job ids', error);
    }
  }, []);

  // Save to session storage
  useEffect(() => {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(jobOrder));
  }, [jobOrder]);

  const fetchJobs = async () => {
    if (jobOrder.length === 0) return {};
    const joinedIds = jobOrder.join(',');
    const response = await fetchWithAuth(`/api/process/jobs/?ids=${encodeURIComponent(joinedIds)}`);
    if (!response.ok) throw new Error(`Failed to fetch jobs: ${response.statusText}`);
    const data = await response.json();
    
    // Transform array to object keyed by ID
    return (data.jobs || []).reduce((acc, job) => {
      acc[job.id] = job;
      return acc;
    }, {});
  };

  const { 
    data: jobs = {}, 
    error: queryError, 
    isLoading: isQueryLoading,
    dataUpdatedAt 
  } = useQuery({
    queryKey: ['processorJobs', jobOrder.join(',')], // Key depends on job IDs
    queryFn: fetchJobs,
    enabled: isAuthenticated && jobOrder.length > 0 && isDrawerOpen,
    refetchInterval: (query) => {
      // Check if we have any jobs in the data that are unfinished
      const currentJobs = query.state.data || {};
      const hasUnfinished = Object.values(currentJobs).some(job => !job.finished_at);
      // Poll if drawer is open AND there are unfinished jobs
      return (isDrawerOpen && hasUnfinished) ? POLLING_INTERVAL_MS : false;
    },
    refetchOnWindowFocus: isDrawerOpen, // Only refetch on focus if drawer is open
    placeholderData: (previousData) => previousData // Keep previous data while fetching new
  });

  const addJob = useCallback((jobId, metadata = null) => {
    if (!jobId) return;

    setJobOrder((previous) => {
      if (previous.includes(jobId)) return previous;
      return [jobId, ...previous];
    });

    // Optimistically update the cache
    queryClient.setQueryData(['processorJobs', [jobId, ...jobOrder].join(',')], (oldData = {}) => ({
      ...oldData,
      [jobId]: {
        id: jobId,
        state: 'queued',
        progress: 0,
        payload: metadata,
        created_at: metadata?.requested_at ?? null,
        // Preserve unfinished status until fetched
        finished_at: null 
      }
    }));
  }, [jobOrder, queryClient]);

  const removeJob = useCallback((jobId) => {
    setJobOrder((previous) => previous.filter((id) => id !== jobId));
    // We don't necessarily need to remove from cache immediately as the key changes,
    // but cleaning up is good practice if we want to manipulate cache directly.
    // However, since queryKey includes the list of IDs, changing jobOrder changes the key,
    // so a new query will start. To avoid flash, we rely on placeholderData or cache sharing?
    // Actually, simpler: let the new key fetch fresh data, or pre-seed it.
    // Since 'jobOrder' is part of the key, removing an ID creates a new cache entry.
    // Ideally we would keep jobs in a separate cache independent of the list order,
    // but for simplicity here, relying on re-fetch is acceptable or we can decouple key.
  }, []);

  const clearJobs = useCallback(() => {
    setJobOrder([]);
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const toggleDrawer = useCallback(() => setIsDrawerOpen(prev => !prev), []);
  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  useEffect(() => {
    if (!isAuthenticated) clearJobs();
  }, [isAuthenticated, clearJobs]);

  const activeJobCount = useMemo(() => {
    return Object.values(jobs).reduce((count, job) => {
      if (['succeeded', 'failed'].includes(job.state)) return count;
      return count + 1;
    }, 0);
  }, [jobs]);

  const value = useMemo(() => ({
    jobs,
    jobOrder,
    isDrawerOpen,
    isPolling: false, // React Query handles this internally now
    isLoading: isQueryLoading,
    lastUpdated: dataUpdatedAt ? new Date(dataUpdatedAt).toISOString() : null,
    lastError: queryError?.message || null,
    activeJobCount,
    addJob,
    removeJob,
    clearJobs,
    refreshJobs: () => queryClient.invalidateQueries({ queryKey: ['processorJobs'] }), // Manual refresh
    toggleDrawer,
    openDrawer,
    closeDrawer
  }), [
    jobs, jobOrder, isDrawerOpen, isQueryLoading, dataUpdatedAt, queryError,
    activeJobCount, addJob, removeJob, clearJobs, toggleDrawer, openDrawer, closeDrawer, queryClient
  ]);

  return <JobStatusContext.Provider value={value}>{children}</JobStatusContext.Provider>;
};
